import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { ShowStructure, Act, Scene, ScriptBlock, Character } from './types';
import { linkBlocksToStructure, normalizeCharacterId } from './utils/deriveBlockLinks';
import { orderScenesByShowStructure } from './utils/scene-order';
import { createLastModifiedBy } from '../../utils/lastModifiedBy';
import { logger } from '../../utils/logger';
import { mapClassifiedCharacters } from './utils/classified-structure';

export interface ShowStructureSlice {
    showStructure: ShowStructure;
    activeJobId: string | null;  // Persist job ID to prevent stalemate on refresh
    showStructureActions: {
        setActiveJobId: (id: string | null) => void;

        // Structure Management
        setStructure: (acts: Act[], scenes: Scene[]) => void;
        addAct: (name: string) => void;
        addScene: (actId: string, name: string) => void;
        updateAct: (actId: string, updates: Partial<Act>) => void;
        deleteAct: (actId: string) => void;
        updateScene: (sceneId: string, updates: Partial<Scene>) => void;
        deleteScene: (sceneId: string) => void;
        moveScene: (actId: string, sceneId: string, newIndex: number) => void;
        
        // Script Block Management
        setScriptBlocks: (blocks: ScriptBlock[]) => void;
        updateScriptBlock: (blockId: string, updates: Partial<ScriptBlock>) => void;
        deleteScriptBlock: (blockId: string) => void;
        splitScriptBlock: (blockId: string, splitIndex: number) => void;
        mergeScriptBlocks: (blockId: string, direction: 'prev' | 'next') => void;
        revertScriptBlock: (blockId: string) => void;
        verifyScriptBlock: (blockId: string) => void;
        bulkVerifyScriptBlocks: (blockIds: string[]) => void;
        bulkUpdateScriptBlocks: (blockIds: string[], updates: Partial<ScriptBlock>) => void;
        bulkDeleteScriptBlocks: (blockIds: string[]) => void;
        
        // Character Management (derived from script blocks)
        setCharacters: (characters: Character[]) => void;
        deriveCharactersFromBlocks: () => void;
        updateCharacter: (characterId: string, updates: Partial<Character>) => void;
        setCharacterHints: (hints: Record<string, { isEnsemble: boolean; notes?: string }>) => void;
        
        // Apply Pro-classified structure (Pass 2 results)
        applyClassifiedStructure: (structure: {
            acts?: Array<{ id: string; name: string; isPreset: boolean; sceneIds: string[] }>;
            scenes?: Array<{ id: string; name: string; notes?: string; type?: 'scene' | 'song'; songNumber?: string; parentSceneId?: string | null }>;
            characters?: Array<{ name: string; isEnsemble: boolean; notes?: string; sceneIds: string[] }>;
        }) => void;
        
        // Reactive Derivation (Phase B)
        deriveStructureFromBlocks: () => void;
        
        // Import from AI extraction
        importFromExtraction: (data: {
            acts: Act[];
            scenes: Scene[];
            characters?: Character[];
            scriptBlocks: ScriptBlock[];
        }, options?: {
            mode?: 'replace' | 'append';
            characterMap?: Map<string, string>; // AI characterId -> existing characterId
        }) => void;

        appendImportedStructure: (data: {
            acts: Act[];
            scenes: Scene[];
            characters?: Array<{ name: string; isEnsemble: boolean; notes?: string; sceneIds: string[] }>;
            scriptBlocks: ScriptBlock[];
        }, options?: {
            idPrefix?: string;
            characterMap?: Map<string, string>;
        }) => void;
        
        // Append blocks for additional pages
        appendScriptBlocks: (
            blocks: ScriptBlock[], 
            pageOffset: number,
            characterMap?: Map<string, string>
        ) => void;

        // Clear all structure data (for fresh imports)
        clearStructure: () => void;
    };
}

function normalizeSceneArrayOrder(showStructure: ShowStructure): void {
    const sceneById = new Map(showStructure.scenes.map(scene => [scene.id, scene] as const));
    const orderedSceneIds = orderScenesByShowStructure(showStructure.acts, showStructure.scenes, {
        includePreset: true,
    }).map(scene => scene.id);
    const orderedSceneIdSet = new Set(orderedSceneIds);
    const orderedScenes = orderedSceneIds
        .map(sceneId => sceneById.get(sceneId))
        .filter((scene): scene is Scene => Boolean(scene));
    const remainingScenes = showStructure.scenes.filter(scene => !orderedSceneIdSet.has(scene.id));

    showStructure.scenes = [...orderedScenes, ...remainingScenes];
}

function hasUsableStructureForBlockLinking(showStructure: ShowStructure): boolean {
    const liveSceneIds = new Set(
        showStructure.scenes
            .filter(scene => !scene.deleted)
            .map(scene => scene.id)
    );

    return showStructure.acts
        .filter(act => !act.deleted && !act.isPreset)
        .some(act => (act.sceneIds || []).some(sceneId => liveSceneIds.has(sceneId)));
}

function materializeScriptBlockStructureAssignments(showStructure: ShowStructure): void {
    const blocks = showStructure.scriptBlocks;
    if (!blocks?.length || !hasUsableStructureForBlockLinking(showStructure)) return;

    showStructure.scriptBlocks = linkBlocksToStructure(
        [...blocks],
        showStructure.acts,
        showStructure.scenes,
        showStructure.characters
    );
}

export const createShowStructureSlice: StateCreator<
    AppState,
    [["zustand/immer", never]],
    [],
    ShowStructureSlice
> = (set) => ({

    showStructure: {
        acts: [
            // Default Preset act for unassigned props, sound cues, etc.
            {
                id: 'preset-act',
                name: 'Preset',
                isPreset: true,
                sceneIds: []
            }
        ],
        scenes: [],
        characters: [],
        scriptBlocks: []
    },
    activeJobId: null,
    showStructureActions: {
        setActiveJobId: (id) => set((state) => {
            state.activeJobId = id;
        }),

        setStructure: (acts, scenes) => set((state) => {
            state.showStructure.acts = acts;
            state.showStructure.scenes = scenes;
            normalizeSceneArrayOrder(state.showStructure);
            materializeScriptBlockStructureAssignments(state.showStructure);
        }),
        
        addAct: (name) => set((state) => {
            const lastModifiedBy = createLastModifiedBy(
                state.auth.user,
                state.cloud.member
            );
            
            const newAct: Act = {
                id: crypto.randomUUID(),
                name,
                isPreset: false,
                sceneIds: [],
                lastModifiedBy
            };
            state.showStructure.acts.push(newAct);
        }),
        
        addScene: (actId, name) => set((state) => {
            const act = state.showStructure.acts.find(a => a.id === actId);
            if (!act) return;
            
            const lastModifiedBy = createLastModifiedBy(
                state.auth.user,
                state.cloud.member
            );
            
            // addScene always creates a dialogue scene (type: 'scene').
            // Songs are created via AI extraction or applyClassifiedStructure.
            const newScene: Scene = {
                id: crypto.randomUUID(),
                name,
                linkedSetId: null,
                notes: '',
                lastModifiedBy
            };
            state.showStructure.scenes.push(newScene);
            act.sceneIds.push(newScene.id);
            normalizeSceneArrayOrder(state.showStructure);
        }),
        
        updateAct: (actId, updates) => set((state) => {
            const act = state.showStructure.acts.find(a => a.id === actId);
            if (act) {
                const lastModifiedBy = createLastModifiedBy(
                    state.auth.user,
                    state.cloud.member
                );
                Object.assign(act, updates, { lastModifiedBy });
            }
        }),
        
        deleteAct: (actId) => set((state) => {
            const index = state.showStructure.acts.findIndex(a => a.id === actId);
            if (index !== -1) {
                const act = state.showStructure.acts[index]!;
                if (act.sceneIds) {
                    for (const sid of act.sceneIds) {
                        const sIdx = state.showStructure.scenes.findIndex(s => s.id === sid);
                        if (sIdx !== -1) state.showStructure.scenes.splice(sIdx, 1);
                        // Also update scriptBlocks that reference this scene
                        if (state.showStructure.scriptBlocks) {
                            for (const block of state.showStructure.scriptBlocks) {
                                if (block.sceneId === sid) {
                                    block.sceneId = null;
                                }
                            }
                        }
                    }
                }
                // Update scriptBlocks that reference this act
                if (state.showStructure.scriptBlocks) {
                    for (const block of state.showStructure.scriptBlocks) {
                        if (block.actId === actId) {
                            block.actId = null;
                        }
                    }
                }
                state.showStructure.acts.splice(index, 1);
                normalizeSceneArrayOrder(state.showStructure);
            }
        }),
        
        updateScene: (sceneId, updates) => set((state) => {
            const scene = state.showStructure.scenes.find(s => s.id === sceneId);
            if (scene) {
                const lastModifiedBy = createLastModifiedBy(
                    state.auth.user,
                    state.cloud.member
                );
                Object.assign(scene, updates, { lastModifiedBy });
            }
        }),
        
        deleteScene: (sceneId) => set((state) => {
            // Cascade: delete child songs that reference this scene as parent
            const childSongs = state.showStructure.scenes.filter(
                s => s.parentSceneId === sceneId
            );
            for (const child of childSongs) {
                // Remove child song from acts
                for (const act of state.showStructure.acts) {
                    act.sceneIds = act.sceneIds.filter(id => id !== child.id);
                }
                // Unlink blocks referencing child song
                for (const block of state.showStructure.scriptBlocks ?? []) {
                    if (block.sceneId === child.id) block.sceneId = null;
                }
            }
            // Remove children from scenes array
            state.showStructure.scenes = state.showStructure.scenes.filter(
                s => s.parentSceneId !== sceneId
            );

            // Remove from acts
            for (const act of state.showStructure.acts) {
                if (act.sceneIds.includes(sceneId)) {
                    act.sceneIds = act.sceneIds.filter(id => id !== sceneId);
                }
            }
            // Remove scene
            const index = state.showStructure.scenes.findIndex(s => s.id === sceneId);
            if (index !== -1) state.showStructure.scenes.splice(index, 1);
            
            // Update scriptBlocks that reference this scene
            if (state.showStructure.scriptBlocks) {
                for (const block of state.showStructure.scriptBlocks) {
                    if (block.sceneId === sceneId) {
                        block.sceneId = null;
                    }
                }
            }
            normalizeSceneArrayOrder(state.showStructure);
        }),
        
        moveScene: (actId, sceneId, newIndex) => set((state) => {
            materializeScriptBlockStructureAssignments(state.showStructure);

            const targetAct = state.showStructure.acts.find(a => a.id === actId);
            const scene = state.showStructure.scenes.find(s => s.id === sceneId && !s.deleted);
            if (!targetAct || !scene) return;

            let wasAssigned = false;
            for (const act of state.showStructure.acts) {
                if (!act.sceneIds.includes(sceneId)) continue;
                act.sceneIds = act.sceneIds.filter(id => id !== sceneId);
                wasAssigned = true;
            }
            if (!wasAssigned) return;

            const boundedIndex = Number.isFinite(newIndex)
                ? Math.max(0, Math.min(newIndex, targetAct.sceneIds.length))
                : targetAct.sceneIds.length;
            targetAct.sceneIds.splice(boundedIndex, 0, sceneId);

            for (const block of state.showStructure.scriptBlocks ?? []) {
                if (block.sceneId === sceneId) {
                    block.actId = targetAct.id;
                }
            }
            normalizeSceneArrayOrder(state.showStructure);
        }),
        
        // Script Block Actions
        setScriptBlocks: (blocks) => {
            set((state) => {
                state.showStructure.scriptBlocks = blocks;
                materializeScriptBlockStructureAssignments(state.showStructure);
            });
        },
        
        updateScriptBlock: (blockId, updates) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            const block = state.showStructure.scriptBlocks.find(b => b.id === blockId);
            if (block) {
                // Store original text if not already stored
                if (updates.text && !block.originalText) {
                    block.originalText = block.text;
                }
                Object.assign(block, updates);
            }
        }),
        
        deleteScriptBlock: (blockId) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            const index = state.showStructure.scriptBlocks.findIndex(b => b.id === blockId);
            if (index !== -1) {
                state.showStructure.scriptBlocks.splice(index, 1);
            }
        }),
        
        splitScriptBlock: (blockId, splitIndex) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            const blockIndex = state.showStructure.scriptBlocks.findIndex(b => b.id === blockId);
            if (blockIndex === -1) return;
            
            const block = state.showStructure.scriptBlocks[blockIndex]!;
            const originalText = block.text;
            
            if (splitIndex <= 0 || splitIndex >= originalText.length) return;
            
            // Create two new blocks from the split
            const firstPart = originalText.slice(0, splitIndex).trim();
            const secondPart = originalText.slice(splitIndex).trim();
            
            if (!firstPart || !secondPart) return;
            
            // Update the original block with first part
            block.text = firstPart;
            block.originalText = originalText;
            block.isVerified = false;
            
            // Create new block for second part
            const newBlock: ScriptBlock = {
                id: crypto.randomUUID(),
                pageNumber: block.pageNumber,
                blockIndex: block.blockIndex + 0.5, // Will be renumbered later
                type: block.type,
                text: secondPart,
                actId: block.actId,
                sceneId: block.sceneId,
                characterId: block.characterId,
                isVerified: false,
                originalText: originalText
            };
            
            // Insert new block after the original
            state.showStructure.scriptBlocks.splice(blockIndex + 1, 0, newBlock);
            
            // Renumber block indices for this page
            const pageBlocks = state.showStructure.scriptBlocks
                .filter(b => b.pageNumber === block.pageNumber)
                .toSorted((a, b) => a.blockIndex - b.blockIndex);
            
            for (const [idx, b] of pageBlocks.entries()) {
                b.blockIndex = idx;
            }
        }),
        
        verifyScriptBlock: (blockId) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            const block = state.showStructure.scriptBlocks.find(b => b.id === blockId);
            if (block) {
                block.isVerified = true;
            }
        }),
        
        bulkVerifyScriptBlocks: (blockIds) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            for (const block of state.showStructure.scriptBlocks) {
                if (blockIds.includes(block.id)) {
                    block.isVerified = true;
                }
            }
        }),
        
        bulkUpdateScriptBlocks: (blockIds, updates) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            const idSet = new Set(blockIds);
            for (const block of state.showStructure.scriptBlocks) {
                if (idSet.has(block.id)) {
                    // Preserve original text if text is being changed
                    if (updates.text && !block.originalText) {
                        block.originalText = block.text;
                    }
                    Object.assign(block, updates);
                }
            }
        }),
        
        bulkDeleteScriptBlocks: (blockIds) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            const idSet = new Set(blockIds);
            state.showStructure.scriptBlocks = state.showStructure.scriptBlocks.filter(
                block => !idSet.has(block.id)
            );
        }),
        
        mergeScriptBlocks: (blockId, direction) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            
            // Sort blocks by page and index to ensure correct order
            const sortedBlocks = [...state.showStructure.scriptBlocks].toSorted((a, b) => 
                a.pageNumber === b.pageNumber 
                    ? a.blockIndex - b.blockIndex 
                    : a.pageNumber - b.pageNumber
            );
            
            const currentIndex = sortedBlocks.findIndex(b => b.id === blockId);
            if (currentIndex === -1) return;
            
            const current = sortedBlocks[currentIndex]!;
            const adjacentIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
            
            if (adjacentIndex < 0 || adjacentIndex >= sortedBlocks.length) return;
            
            const adjacent = sortedBlocks[adjacentIndex]!;
            
            // Merge text: remove trailing hyphen if present at join point
            let mergedText: string;
            if (direction === 'prev') {
                // Merge previous into current: prev.text + current.text
                const prevText = adjacent.text.replace(/-\s*$/, ''); // Remove trailing hyphen
                mergedText = prevText + current.text;
            } else {
                // Merge next into current: current.text + next.text
                const currentText = current.text.replace(/-\s*$/, ''); // Remove trailing hyphen
                mergedText = currentText + adjacent.text;
            }
            
            // Store original text if not already stored
            const actualBlock = state.showStructure.scriptBlocks.find(b => b.id === blockId)!;
            if (!actualBlock.originalText) {
                actualBlock.originalText = actualBlock.text;
            }
            
            // Update current block with merged text
            actualBlock.text = mergedText;
            actualBlock.isVerified = true;
            
            // Delete the adjacent block
            const deleteId = adjacent.id;
            const deleteIndex = state.showStructure.scriptBlocks.findIndex(b => b.id === deleteId);
            if (deleteIndex !== -1) {
                state.showStructure.scriptBlocks.splice(deleteIndex, 1);
            }
        }),
        
        revertScriptBlock: (blockId) => set((state) => {
            if (!state.showStructure.scriptBlocks) return;
            const block = state.showStructure.scriptBlocks.find(b => b.id === blockId);
            if (block?.originalText) {
                block.text = block.originalText;
                block.originalText = undefined;
                block.isVerified = false;
            }
        }),
        
        // Character Actions
        setCharacters: (characters) => set((state) => {
            state.showStructure.characters = characters;
        }),
        
        // Client-side fallback: derive characters from character_name blocks
        deriveCharactersFromBlocks: () => set((state) => {
            const blocks = state.showStructure.scriptBlocks;
            if (!blocks || blocks.length === 0) return;

            // Collect unique characters with their scene appearances
            const characterMap = new Map<string, { name: string; sceneIds: Set<string> }>();

            for (const block of blocks) {
                if (block.type !== 'character_name') continue;

                // Normalize: trim, collapse whitespace, strip trailing parentheticals like "(V.O.)"
                const rawName = block.text.trim().replace(/\s+/g, ' ').replace(/\s*\(.*\)\s*$/, '').trim();
                if (!rawName) continue;

                const key = rawName.toUpperCase();

                if (!characterMap.has(key)) {
                    characterMap.set(key, { name: rawName, sceneIds: new Set() });
                }

                // Assign to scene if the block has a sceneId (set by linkBlocksToStructure)
                if (block.sceneId) {
                    characterMap.get(key)!.sceneIds.add(block.sceneId);
                }
            }

            // Convert to Character objects
            state.showStructure.characters = Array.from(characterMap.values()).map(({ name, sceneIds }) => ({
                id: `char-${crypto.randomUUID()}`,
                name,
                isEnsemble: false,
                parentId: null,
                sceneIds: Array.from(sceneIds),
            }));

            logger.store(`[deriveCharactersFromBlocks] Derived ${state.showStructure.characters.length} characters from blocks`);
        }),
        
        updateCharacter: (characterId, updates) => set((state) => {
            const character = state.showStructure.characters.find(c => c.id === characterId);
            if (character) {
                const lastModifiedBy = createLastModifiedBy(
                    state.auth.user,
                    state.cloud.member
                );
                Object.assign(character, updates, { lastModifiedBy });
            }
        }),
        
        setCharacterHints: (hints) => set((state) => {
            state.showStructure.characterHints = hints;
        }),
        
        applyClassifiedStructure: (structure) => set((state) => {
            logger.store('[applyClassifiedStructure] Applying Pro-classified structure:', 
                structure.acts?.length || 0, 'acts,', 
                structure.scenes?.length || 0, 'scenes,',
                structure.characters?.length || 0, 'characters'
            );
            
            // Deduplicate acts and scenes by ID (AI may extract duplicates from TOC + body)
            const deduplicatedActs = (structure.acts || []).filter(
                (act, index, self) => self.findIndex(a => a.id === act.id) === index
            );
            const deduplicatedScenes = (structure.scenes || []).filter(
                (scene, index, self) => self.findIndex(s => s.id === scene.id) === index
            );
            
            // Keep the preset act and merge with classified acts
            // Keep the preset act and merge with classified acts, ensuring NO duplicates
            const presetAct = state.showStructure.acts.find(a => a.isPreset);
            let newActs = deduplicatedActs;

            if (presetAct) {
                // Filter out any AI-derived act that clashes with the preset Act ID
                const nonConflictingActs = deduplicatedActs.filter(a => a.id !== presetAct.id);
                newActs = [presetAct, ...nonConflictingActs];
            }
            
            state.showStructure.acts = newActs;
            state.showStructure.scenes = deduplicatedScenes.map(s => ({
                ...s,
                linkedSetId: null,
                notes: s.notes || '',
                type: s.type || 'scene',
                songNumber: s.songNumber || undefined,
                parentSceneId: s.parentSceneId ?? null,
            }));
            normalizeSceneArrayOrder(state.showStructure);
            
            // Map classified characters to store state
            logger.store('[applyClassifiedStructure] Input characters:', 
                JSON.stringify(structure.characters?.slice(0, 3), null, 2)); // First 3 for brevity
            logger.store('[applyClassifiedStructure] Scenes being set:',
                deduplicatedScenes.map(s => s.id).join(', '));
            
            state.showStructure.characters = mapClassifiedCharacters(structure.characters);
            
            logger.store('[applyClassifiedStructure] Characters after mapping:',
                state.showStructure.characters.slice(0, 3).map(c => 
                    `${c.name}: sceneIds=[${c.sceneIds.join(', ')}]`
                ).join('; '));
            
            // Link blocks to canonical structure IDs now that we have both
            materializeScriptBlockStructureAssignments(state.showStructure);
        }),
        
        // Phase B: Client-side fallback — derive acts/scenes from heading blocks
        deriveStructureFromBlocks: () => set((state) => {
            const blocks = state.showStructure.scriptBlocks;
            if (!blocks || blocks.length === 0) return;

            const acts: Act[] = [];
            const scenes: Scene[] = [];
            let currentAct: Act | null = null;

            for (const block of blocks) {
                if (block.type !== 'heading') continue;
                const text = block.text.trim();
                const upper = text.toUpperCase();

                // Detect ACT headings
                if (upper.includes('ACT') && /ACT\s*[IVXLCDM0-9]+/i.test(upper)) {
                    currentAct = {
                        id: crypto.randomUUID(),
                        name: text,
                        isPreset: false,
                        sceneIds: [],
                    };
                    acts.push(currentAct);
                    continue;
                }

                // Detect SCENE headings ("SCENE", "Scene 1", "#1 -", "1.", numbered patterns)
                const isScene = upper.includes('SCENE')
                    || /^#?\d+\s*[-–—.]/.test(text)
                    || /^SCENE\s/i.test(text);

                if (isScene) {
                    const scene: Scene = {
                        id: crypto.randomUUID(),
                        name: text,
                        linkedSetId: null,
                        notes: '',
                    };
                    scenes.push(scene);

                    // Link scene to current act
                    if (currentAct) {
                        currentAct.sceneIds.push(scene.id);
                    }
                }
            }

            // Edge case: scenes exist but no acts detected (one-act play)
            if (scenes.length > 0 && acts.length === 0) {
                const fullShowAct: Act = {
                    id: crypto.randomUUID(),
                    name: 'Full Show',
                    isPreset: false,
                    sceneIds: scenes.map(s => s.id),
                };
                acts.push(fullShowAct);
            }

            // Preserve existing preset act, append derived acts
            const presetAct = state.showStructure.acts.find(a => a.isPreset);
            state.showStructure.acts = presetAct ? [presetAct, ...acts] : acts;
            state.showStructure.scenes = scenes;
            normalizeSceneArrayOrder(state.showStructure);
            materializeScriptBlockStructureAssignments(state.showStructure);

            logger.store(`[deriveStructureFromBlocks] Derived ${acts.length} acts, ${scenes.length} scenes from blocks`);
        }),
        
        importFromExtraction: (data, options = {}) => set((state) => {
            const { mode = 'replace', characterMap } = options;
            
            // Helper to apply character mapping to blocks
            const mapBlockCharacters = (blocks: ScriptBlock[]): ScriptBlock[] => {
                if (!characterMap) return blocks;
                return blocks.map(block => ({
                    ...block,
                    characterId: block.characterId && characterMap.get(block.characterId) 
                        ? characterMap.get(block.characterId)! 
                        : block.characterId
                }));
            };
            
            if (mode === 'append') {
                // APPEND MODE: Merge new data with existing
                const existingBlocks = state.showStructure.scriptBlocks || [];
                const maxPage = existingBlocks.reduce((max, b) => Math.max(max, b.pageNumber), 0);
                
                // Offset new block page numbers to come after existing content
                const mappedBlocks = mapBlockCharacters(data.scriptBlocks).map(block => ({
                    ...block,
                    id: crypto.randomUUID(), // Ensure unique IDs
                    pageNumber: block.pageNumber + maxPage
                }));
                
                state.showStructure.scriptBlocks = [...existingBlocks, ...mappedBlocks];
                
                // Merge acts (keep existing, add new with unique names)
                const existingActNames = new Set(state.showStructure.acts.map(a => a.name.toLowerCase()));
                for (const act of data.acts) {
                    if (!existingActNames.has(act.name.toLowerCase())) {
                        state.showStructure.acts.push({
                            ...act,
                            id: crypto.randomUUID()
                        });
                    }
                }
                
                // Merge scenes (keep existing, add new with unique names)
                const existingSceneNames = new Set(state.showStructure.scenes.map(s => s.name.toLowerCase()));
                for (const scene of data.scenes) {
                    if (!existingSceneNames.has(scene.name.toLowerCase())) {
                        state.showStructure.scenes.push({
                            ...scene,
                            id: crypto.randomUUID()
                        });
                    }
                }
                
                // Merge characters (keep existing, add new with unique names)
                if (data.characters) {
                    const existingCharNames = new Set(
                        state.showStructure.characters.map(c => c.name.toLowerCase())
                    );
                    for (const char of data.characters) {
                        if (!existingCharNames.has(char.name.toLowerCase())) {
                            state.showStructure.characters.push({
                                ...char,
                                id: crypto.randomUUID()
                            });
                        }
                    }
                }
                normalizeSceneArrayOrder(state.showStructure);
                materializeScriptBlockStructureAssignments(state.showStructure);
            } else {
                // REPLACE MODE: Overwrite existing data (original behavior)
                const presetAct = state.showStructure.acts.find(a => a.isPreset);
                
                state.showStructure.acts = presetAct 
                    ? [presetAct, ...data.acts.filter(a => !a.isPreset)]
                    : data.acts;
                state.showStructure.scenes = data.scenes;
                normalizeSceneArrayOrder(state.showStructure);
                state.showStructure.scriptBlocks = mapBlockCharacters(data.scriptBlocks);
                
                if (data.characters && data.characters.length > 0) {
                    state.showStructure.characters = data.characters;
                }
                materializeScriptBlockStructureAssignments(state.showStructure);
            }
        }),

        appendImportedStructure: (data, options = {}) => set((state) => {
            const idPrefix = options.idPrefix || crypto.randomUUID();
            const sceneIdMap = new Map<string, string>();
            const actIdMap = new Map<string, string>();
            const existingPageMax = (state.showStructure.scriptBlocks || [])
                .reduce((max, block) => Math.max(max, block.pageNumber || 0), 0);

            const createMappedId = (kind: 'act' | 'scene' | 'block', id: string) =>
                `${kind}-${idPrefix}-${id}`;
            const createMappedCharacterId = (name: string) =>
                `character-${idPrefix}-${normalizeCharacterId(name)}`;
            const normalizeCharacterName = (value: string) =>
                value.trim().replace(/\s+/g, ' ').toUpperCase();
            const addCharacterMapping = (map: Map<string, string>, value: string, characterId: string) => {
                map.set(normalizeCharacterName(value), characterId);
                map.set(normalizeCharacterId(value), characterId);
            };

            for (const scene of data.scenes || []) {
                sceneIdMap.set(scene.id, createMappedId('scene', scene.id));
            }
            for (const act of data.acts || []) {
                if (!act.isPreset) {
                    actIdMap.set(act.id, createMappedId('act', act.id));
                }
            }

            const appendedScenes: Scene[] = (data.scenes || []).map(scene => ({
                ...scene,
                id: sceneIdMap.get(scene.id)!,
                parentSceneId: scene.parentSceneId ? sceneIdMap.get(scene.parentSceneId) ?? null : null,
                linkedSetId: scene.linkedSetId ?? null,
                notes: scene.notes || '',
                type: scene.type || 'scene',
                songNumber: scene.songNumber || undefined,
            }));

            const appendedActs: Act[] = (data.acts || [])
                .filter(act => !act.isPreset)
                .map(act => ({
                    ...act,
                    id: actIdMap.get(act.id)!,
                    isPreset: false,
                    sceneIds: (act.sceneIds || [])
                        .map(sceneId => sceneIdMap.get(sceneId))
                        .filter((sceneId): sceneId is string => Boolean(sceneId)),
                }));

            const characterNameIdMap = new Map<string, string>();
            const usedCharacterIds = new Set(state.showStructure.characters.map(character => character.id));
            const appendedCharacters: Character[] = [];
            for (const character of data.characters || []) {
                const normalizedName = normalizeCharacterName(character.name);
                const mappedExistingId = options.characterMap?.get(character.name)
                    ?? options.characterMap?.get(normalizedName)
                    ?? options.characterMap?.get(normalizeCharacterId(character.name));

                if (mappedExistingId) {
                    addCharacterMapping(characterNameIdMap, character.name, mappedExistingId);
                    continue;
                }

                const baseCharacterId = createMappedCharacterId(character.name);
                let characterId = baseCharacterId;
                let suffix = 2;
                while (usedCharacterIds.has(characterId)) {
                    characterId = `${baseCharacterId}-${suffix}`;
                    suffix += 1;
                }
                usedCharacterIds.add(characterId);
                addCharacterMapping(characterNameIdMap, character.name, characterId);
                appendedCharacters.push({
                    id: characterId,
                    name: character.name,
                    isEnsemble: character.isEnsemble || false,
                    parentId: null,
                    notes: character.notes || '',
                    sceneIds: (character.sceneIds || [])
                        .map(sceneId => sceneIdMap.get(sceneId))
                        .filter((sceneId): sceneId is string => Boolean(sceneId)),
                });
            }

            const mapBlockCharacterId = (block: ScriptBlock): string | null => {
                if (block.characterId && options.characterMap?.get(block.characterId)) {
                    return options.characterMap.get(block.characterId)!;
                }

                if (block.characterId) {
                    const byCharacterIdText = characterNameIdMap.get(normalizeCharacterName(block.characterId))
                        ?? characterNameIdMap.get(normalizeCharacterId(block.characterId));
                    if (byCharacterIdText) return byCharacterIdText;
                }

                if (block.type === 'character_name') {
                    return characterNameIdMap.get(normalizeCharacterName(block.text))
                        ?? characterNameIdMap.get(normalizeCharacterId(block.text))
                        ?? null;
                }

                return null;
            };

            const mappedBlocks: ScriptBlock[] = (data.scriptBlocks || []).map(block => ({
                ...block,
                id: createMappedId('block', block.id),
                pageNumber: block.pageNumber + existingPageMax,
                actId: block.actId ? actIdMap.get(block.actId) ?? null : null,
                sceneId: block.sceneId ? sceneIdMap.get(block.sceneId) ?? null : null,
                characterId: mapBlockCharacterId(block),
            }));

            const linkedBlocks = linkBlocksToStructure(mappedBlocks, appendedActs, appendedScenes);

            state.showStructure.acts.push(...appendedActs);
            state.showStructure.scenes.push(...appendedScenes);
            state.showStructure.scriptBlocks = [
                ...(state.showStructure.scriptBlocks || []),
                ...linkedBlocks,
            ];

            state.showStructure.characters.push(...appendedCharacters);
            normalizeSceneArrayOrder(state.showStructure);
        }),
        
        appendScriptBlocks: (blocks, pageOffset, characterMap) => set((state) => {
            const existingBlocks = state.showStructure.scriptBlocks || [];
            
            // Apply character mapping and page offset
            const mappedBlocks = blocks.map(block => ({
                ...block,
                id: crypto.randomUUID(),
                pageNumber: block.pageNumber + pageOffset,
                characterId: block.characterId && characterMap?.get(block.characterId)
                    ? characterMap.get(block.characterId)!
                    : block.characterId
            }));
            
            // Combine blocks, then materialize links if the canonical structure is already available.
            state.showStructure.scriptBlocks = [...existingBlocks, ...mappedBlocks];
            materializeScriptBlockStructureAssignments(state.showStructure);
        }),

        clearStructure: () => set((state) => {
            state.showStructure = {
                acts: [{
                    id: 'preset-act',
                    name: 'Preset',
                    isPreset: true,
                    sceneIds: []
                }],
                scenes: [],
                characters: [],
                scriptBlocks: []
            };
        })
    }
});
